import { Injectable, OnDestroy } from "@angular/core";
import { AppConfigModel, AppJoinedModel } from "../interface/app-config-model";
import { Observable, BehaviorSubject, of, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { MdbModalConfig } from "mdb-angular-ui-kit/modal";
import { RunningAppModel } from "../interface/running-app-model";
import html2canvas, { Options } from 'html2canvas';
import * as uuid from 'uuid';
import { AppManagerService } from "../appManager/appManager.service";
import { InstalledAppCollection } from "../interface/collection";
import { ProfileUserModel } from "../interface/user-profile-model";

// Declare Meteor as a global to avoid TypeScript errors
declare const Meteor: any;

// Serializable version of running app for database persistence
export interface PersistedAppModel {
    id: string;
    appConfig: AppConfigModel;
    homeView: string;
    createdDate: string;
    lastModified: string;
    isMinimized: boolean;
    isMaximized: boolean;
    screenshot?: string;
    position?: {
        top?: string;
        left?: string;
        width?: string;
        height?: string;
    };
}

@Injectable({
    providedIn: 'root'
})
export class HomeService implements OnDestroy {
    private currentRunningAppSubject: BehaviorSubject<RunningAppModel[]> = new BehaviorSubject<RunningAppModel[]>([]);
    public currentRunningApp$: Observable<RunningAppModel[]> = this.currentRunningAppSubject.asObservable();
    private installedAppSubscription: BehaviorSubject<AppJoinedModel[]> = new BehaviorSubject<AppJoinedModel[]>([]);
    private userProfileSubject: BehaviorSubject<ProfileUserModel> = new BehaviorSubject<ProfileUserModel>({} as ProfileUserModel);

    // Store current user ID for database operations
    private currentUserId: string | null = null;

    // Meteor sync: one autorun + subscription per user, cleaned up on destroy or user change
    private meteorAutorunHandle: { stop: () => void } | null = null;
    private meteorSubscriptionHandle: { ready: () => boolean; stop: () => void } | null = null;
    private currentInstalledAppsUserId: string | null = null;

    // Store apps to restore in memory (replaces localStorage cache)
    private appsToRestoreCache: PersistedAppModel[] = [];
    private appsRestored: boolean = false;

    // Debounce database saves to avoid excessive writes
    private saveSubject = new Subject<void>();
    private destroy$ = new Subject<void>();

    // Popup Overlay Properties
    private popupsSubject: BehaviorSubject<Array<{ id: string; showBackground: boolean; data?: any }>> = new BehaviorSubject<Array<{ id: string; showBackground: boolean; data?: any }>>([]);
    public popups$: Observable<Array<{ id: string; showBackground: boolean; data?: any }>> = this.popupsSubject.asObservable();

    // Request to open Settings popup (e.g. from header profile menu)
    private openSettingsRequestSubject = new Subject<void>();
    public openSettingsRequest$: Observable<void> = this.openSettingsRequestSubject.asObservable();

    constructor(private appManagerService: AppManagerService) {
        // Setup debounced save - only save once per 500ms of activity
        this.saveSubject.pipe(
            debounceTime(500),
            takeUntil(this.destroy$)
        ).subscribe(() => this.performSaveToDatabase());
    }

    /**
     * Set the current user ID for session persistence.
     * Stops installed-apps Meteor sync when user changes or is cleared.
     */
    public setUserId(userId: string | null): void {
        if (this.currentUserId !== userId) {
            this.stopInstalledAppsSync();
            if (!userId) {
                this.installedAppSubscription.next([]);
            }
        }
        this.currentUserId = userId;
        if (userId) {
            this.loadPersistedApps();
        }
    }

    /**
     * Queue a debounced save to database
     */
    private saveToDatabase(): void {
        if (!this.currentUserId) {
            return; // Can't save without user ID
        }
        this.saveSubject.next();
    }

    // Store current desktop number for session persistence
    private currentDesktop: number = 1;

    /**
     * Set the current desktop number
     */
    public setCurrentDesktop(desktopNum: number): void {
        this.currentDesktop = desktopNum;
        this.saveToDatabase();
    }

    /**
     * Get the current desktop number from session
     */
    public getCurrentDesktop(): number {
        return this.currentDesktop;
    }

    /**
     * Actually performs the save to database (called via debounced subject)
     */
    private performSaveToDatabase(): void {
        if (!this.currentUserId) {
            return; // Can't save without user ID
        }

        const apps = this.currentRunningApp;
        const persistedApps: PersistedAppModel[] = apps
            .filter(app => app.config?.data?.name) // Only save apps with valid config
            .map(app => {
                // Extract position from dialog element if available
                let position: { top?: string; left?: string; width?: string; height?: string } | undefined;
                if (app.dialogElm) {
                    const style = window.getComputedStyle(app.dialogElm);
                    position = {
                        top: style.top,
                        left: style.left,
                        width: style.width,
                        height: style.height
                    };
                }

                return {
                    id: app.id || uuid.v4(),
                    appConfig: app.config?.data as AppConfigModel,
                    homeView: app.homeView || 'desktopOne',
                    createdDate: app.createdDate?.toISOString() || new Date().toISOString(),
                    lastModified: app.lastModified?.toISOString() || new Date().toISOString(),
                    isMinimized: app.config?.data?.isMinimized || false,
                    isMaximized: app.config?.data?.isMaximized || false,
                    screenshot: app.screenshot,
                    position: position
                };
            });

        Meteor.call('saveUserSession', this.currentUserId, persistedApps, this.currentDesktop, (error: any, result: any) => {
            if (error) {
                console.error('Failed to save session to database:', error);
            } else if (result?.code !== 200) {
                console.error('Failed to save session:', result?.message);
            }
        });
    }

    /**
     * Loads persisted apps from database
     */
    private loadPersistedApps(): void {
        if (!this.currentUserId) {
            return;
        }

        Meteor.call('getUserSession', this.currentUserId, (error: any, result: any) => {
            if (error) {
                console.error('Failed to load session from database:', error);
            } else if (result?.code === 200 && result?.data) {
                // Store session data for restoration
                const sessionData = result.data;

                // Restore current desktop number
                if (sessionData.currentDesktop) {
                    this.currentDesktop = sessionData.currentDesktop;
                }

                if (sessionData.runningApps && sessionData.runningApps.length > 0) {
                    // Store in memory cache for getAppsToRestore to retrieve
                    this.appsToRestoreCache = sessionData.runningApps;
                    this.appsRestored = false;
                }
            }
        });
    }

    /**
     * Gets the list of apps that need to be restored after a page refresh
     * Returns the persisted app configs from database
     */
    public getAppsToRestore(): PersistedAppModel[] {
        if (!this.currentUserId) {
            return [];
        }

        // Return cached apps if available and not yet restored
        if (this.appsToRestoreCache.length > 0 && !this.appsRestored) {
            const appsToRestore = [...this.appsToRestoreCache];
            // Mark as restored and clear cache
            this.appsRestored = true;
            this.appsToRestoreCache = [];

            // Clear apps from database after restoration
            this.clearRestoredAppsFromDatabase();

            return appsToRestore;
        }

        return [];
    }

    /**
     * Clears restored apps from database after they've been restored
     */
    private clearRestoredAppsFromDatabase(): void {
        if (!this.currentUserId) {
            return;
        }

        // Clear running apps from database session after restoration
        Meteor.call('clearUserSession', this.currentUserId, (error: any, result: any) => {
            if (error) {
                console.error('Failed to clear restored apps from database:', error);
            } else if (result?.code !== 200) {
                console.error('Failed to clear restored apps:', result?.message);
            }
        });
    }

    /**
     * Checks if there are apps waiting to be restored
     */
    public hasAppsToRestore(): boolean {
        return this.appsToRestoreCache.length > 0 && !this.appsRestored;
    }

    /**
     * Get installed apps as an observable, driven by a single Meteor subscription + autorun per user.
     * Sync runs only once per user; switching user or destroy stops the previous sync.
     */
    getInstalledAppsBySubscription(email: string): Observable<AppJoinedModel[]> {
        if (!email || typeof Meteor === 'undefined') {
            return of(this.installedAppSubscription.value);
        }
        this.ensureInstalledAppsSync(email);
        return this.installedAppSubscription.asObservable();
    }

    private ensureInstalledAppsSync(email: string): void {
        if (this.currentInstalledAppsUserId === email) {
            return;
        }
        this.stopInstalledAppsSync();
        this.currentInstalledAppsUserId = email;
        this.meteorSubscriptionHandle = Meteor.subscribe('installedApp', email);
        this.meteorAutorunHandle = Meteor.autorun(() => {
            if (this.meteorSubscriptionHandle?.ready()) {
                const apps = InstalledAppCollection.find({ userId: email }).fetch() as AppJoinedModel[];
                this.installedAppSubscription.next(apps);
            }
        });
    }

    private stopInstalledAppsSync(): void {
        if (this.meteorAutorunHandle) {
            this.meteorAutorunHandle.stop();
            this.meteorAutorunHandle = null;
        }
        if (this.meteorSubscriptionHandle) {
            this.meteorSubscriptionHandle.stop();
            this.meteorSubscriptionHandle = null;
        }
        this.currentInstalledAppsUserId = null;
    }

    public getInstalledApps(userId: string): Observable<AppJoinedModel[]> {
        return this.appManagerService.getInstalledApps(userId);
    }

    public getUserProfile(token: string): Observable<ProfileUserModel> {
        Meteor
            .call('getConsumerProfile', token, (error: any, result: any) => {
                if (error) {
                    this.userProfileSubject.error(error);
                } else {
                    const profile = result.data;
                    this.userProfileSubject.next(profile);
                    // Set user ID for session persistence when profile is loaded
                    if (profile?.profile?.email) {
                        this.setUserId(profile.profile.email);
                    }
                }
            });

        return this.userProfileSubject.asObservable();
    }

    private get currentRunningApp(): RunningAppModel[] {
        return this.currentRunningAppSubject.value;
    }

    public callbackModal(dialogElm: HTMLElement, config: MdbModalConfig, homeView: string): void {
        const apps = [...this.currentRunningApp];
        const currentRunningAppIndex = apps.length;

        if (apps.findIndex((app) => app.config?.data?.name === config.data?.name) === -1) {
            apps[currentRunningAppIndex] = {};
            apps[currentRunningAppIndex].id = uuid.v4();
            apps[currentRunningAppIndex].dialogElm = dialogElm;
            apps[currentRunningAppIndex].config = config;
            apps[currentRunningAppIndex].homeView = homeView;
            apps[currentRunningAppIndex].createdDate = new Date();
            apps[currentRunningAppIndex].lastModified = new Date();
            apps[currentRunningAppIndex].config!.data!.isMaximized = false;
            apps[currentRunningAppIndex].config!.data!.isMinimized = false;
            this.currentRunningAppSubject.next(apps);
            this.saveToDatabase();

            const appId = config.data?._id as string;
            if (appId) {
                setTimeout(() => this.captureAppScreenshot(appId), 2000);
            }
        }
    }

    /**
     * Capture screenshot of app content for footer preview. Updates running app and emits so UI reflects.
     */
    private captureAppScreenshot(appId: string): void {
        const idname = '#app-' + appId;
      const screenshotElement: any = document.querySelector(idname);
        if (!screenshotElement) return;

        const opts: Partial<Options> = {
            logging: false,
            // backgroundColor: '#fff',
            // removeContainer: false,
            // foreignObjectRendering: true,
            // scale: 1.5,
        };

        html2canvas(screenshotElement, opts).then(canvas => {
            canvas.toBlob((blob) => {
                if (!blob) return;
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    const apps = [...this.currentRunningApp];
                    const idx = apps.findIndex((app) => app.config?.data?._id === appId);
                    if (idx !== -1) {
                        apps[idx].screenshot = reader.result as string;
                        this.currentRunningAppSubject.next(apps);
                    }
                };
            }, 'image/jpeg', 0.6);
        }).catch(() => {});
    }

    public updateRunningAppHomeView(name: any, refName: any) {
        const apps = [...this.currentRunningApp];
        const currentRunningAppIndex = apps.findIndex((app) => app.config?.data?.name === name);
        if (currentRunningAppIndex !== -1) {
            apps[currentRunningAppIndex].homeView = refName;
            this.currentRunningAppSubject.next(apps);
            this.saveToDatabase();
        }
    }

    public swapMinimizeModal(id: string, homeView: string): void {
        const apps = [...this.currentRunningApp];

        // Given the name, find the index of the app in the array since it was initialize when openned
        const currentRunningAppIndex = apps.findIndex((app) => app.config?.data?._id === id);

        if (apps[currentRunningAppIndex].config!.data!.isMinimized === false) {
            apps[currentRunningAppIndex].dialogElm!.classList.add('modal-minimized');
            apps[currentRunningAppIndex].dialogElm!.classList.remove('modal-unminimized');
            apps[currentRunningAppIndex].config!.data!.isMinimized = true;

            const idname = 'app-' + id;
            let screenshotElement: HTMLElement = document.getElementById(idname) as HTMLIFrameElement;

            if (screenshotElement) {
                // Use requestIdleCallback to capture screenshot without blocking UI
                const captureScreenshot = () => {
                    const opts: Partial<Options> = {
                        logging: false,
                        backgroundColor: null,
                        removeContainer: true,
                        foreignObjectRendering: true,
                        scale: 0.5, // Reduce scale for faster capture
                    };

                    html2canvas(screenshotElement, opts).then(canvas => {
                        canvas.toBlob((myblob) => {
                            if (myblob) {
                                const reader = new FileReader();
                                reader.readAsDataURL(myblob);
                                reader.onloadend = () => {
                                    apps[currentRunningAppIndex].screenshot = reader.result as string;
                                    this.currentRunningAppSubject.next([...this.currentRunningApp]);
                                }
                            }
                        }, 'image/jpeg', 0.6); // Use JPEG with lower quality for smaller size
                    });
                };

                // Run screenshot capture during idle time to avoid blocking UI
                if ('requestIdleCallback' in window) {
                    (window as any).requestIdleCallback(captureScreenshot, { timeout: 2000 });
                } else {
                    setTimeout(captureScreenshot, 100);
                }
            }

            const parentNode = apps[currentRunningAppIndex].dialogElm!.parentNode as HTMLElement | undefined;
            if (parentNode) {
                setTimeout(() => {
                    parentNode.classList.remove('show');

                    const overlayElement = apps[currentRunningAppIndex]!.overlayRef as HTMLElement | undefined;
                    if (overlayElement) {
                        overlayElement.style.cssText = 'display: none;';
                    }
                }, 100);
            }
        } else {
            apps[currentRunningAppIndex].dialogElm!.classList.remove('modal-minimized');
            apps[currentRunningAppIndex].dialogElm!.classList.add('modal-unminimized');
            apps[currentRunningAppIndex].config!.data!.isMinimized = false;

            const dialogElm = apps[currentRunningAppIndex].dialogElm as HTMLElement | undefined;
            if (dialogElm) {
                dialogElm.classList.add('fadeIn');
            }
        }
        this.currentRunningAppSubject.next(apps);
        this.saveToDatabase();
    }

    public doMaximizeModal(id: string, homeView: string): RunningAppModel {
        const apps = [...this.currentRunningApp];
        const currentRunningAppIndex = apps.findIndex((app) => app.config?.data?._id === id);

        if (currentRunningAppIndex !== -1) {
            // Save current position/size so restore can reapply; add fullscreen class so panel animates from current position to fullscreen
            apps[currentRunningAppIndex].style = apps[currentRunningAppIndex].dialogElm!.style.cssText || '';
            apps[currentRunningAppIndex].dialogElm!.parentElement!.classList.add('my-fullscreen');
            apps[currentRunningAppIndex].config!.data!.isMaximized = true;
            apps[currentRunningAppIndex].config!.data!.isMinimized = false;
            // Do not set panel inline styles here – panel keeps current top/left/width/height and CSS transition animates to fullscreen

            this.currentRunningAppSubject.next(apps);
            this.saveToDatabase();
        }
        return apps[currentRunningAppIndex];
    }

    public doRestoreModal(id: string, homeView: string): RunningAppModel {
        const apps = [...this.currentRunningApp];
        const currentRunningAppIndex = apps.findIndex((app) => app.config?.data?._id === id);

        if (currentRunningAppIndex !== -1) {
            const dialogElm = apps[currentRunningAppIndex].dialogElm!;
            const overlay = dialogElm.parentElement!;
            const savedStyle = apps[currentRunningAppIndex].style || '';
            // Set target state (saved position/size) while still fullscreen so transition has a clear "from"
            dialogElm.style.cssText = savedStyle;
            // Defer class removal by one frame so browser paints fullscreen, then transitions to saved rect
            requestAnimationFrame(() => {
                overlay.classList.remove('my-fullscreen');
                const currentApps = [...this.currentRunningApp];
                const idx = currentApps.findIndex((app) => app.config?.data?._id === id);
                if (idx !== -1) {
                    currentApps[idx].config!.data!.isMaximized = false;
                    currentApps[idx].config!.data!.isMinimized = false;
                    currentApps[idx].style = '';
                }
                this.currentRunningAppSubject.next(currentApps);
                this.saveToDatabase();
            });
        }
        return apps[currentRunningAppIndex];
    }

    public getCurrentRunningAppByName(id: string): RunningAppModel {
        return this.currentRunningApp.find((app) => app?.config?.data?._id === id) || { name: '', config: undefined };
    }

    public stopCurrentRunningAppByName(name: string, homeView: string): void {
        const apps = this.currentRunningApp.filter((app) => app.config?.data?.name !== name || app.homeView !== homeView);
        this.currentRunningAppSubject.next(apps);
        this.saveToDatabase();
    }


    // Popup Overlay Methods
    public togglePopup(popupId: string = 'default', showBackground: boolean = true, data?: any): void {
        const existingIndex = this.popupsSubject.value.findIndex(p => p.id === popupId);
        if (existingIndex >= 0) {
            this.closePopup(popupId);
        } else {
            this.openPopup(popupId, showBackground, data);
        }
    }

    public closePopup(popupId: string = 'default'): void {
        const currentApp: any = this.popupsSubject.value.find(p => p.id === popupId);
        this.popupsSubject.next(this.popupsSubject.value.filter(p => p.id !== popupId));
        const appName = currentApp?.data?.app?.name;
        const homeView = currentApp?.data?.desktopView ?? currentApp?.data?.app?.homeView ?? 'desktopOne';
        if (appName) {
            this.stopCurrentRunningAppByName(appName, homeView);
        }
    }

    public openPopup(popupId: string = 'default', showBackground: boolean = true, data?: any): void {
        // Check if popup already exists
        const existingIndex = this.popupsSubject.value.findIndex(p => p.id === popupId);
        if (existingIndex >= 0) {
            // Update existing popup
            this.popupsSubject.next([...this.popupsSubject.value.slice(0, existingIndex), { id: popupId, showBackground, data }, ...this.popupsSubject.value.slice(existingIndex + 1)]);
        } else {
            // Add new popup
            this.popupsSubject.next([...this.popupsSubject.value, { id: popupId, showBackground, data }]);
        }
    }

    public isPopupOpen(popupId: string = 'default'): boolean {
        return this.popupsSubject.value.some(p => p.id === popupId);
    }

    /**
     * Request that the Settings app popup be opened (consumed by HomeComponent).
     */
    public requestOpenSettings(): void {
        this.openSettingsRequestSubject.next();
    }

    public getPopupData(popupId: string = 'default'): any {
        const popup = this.popupsSubject.value.find(p => p.id === popupId);
        return popup?.data;
    }

    public closeAllPopups(): void {
        this.popupsSubject.next([]);
    }



    ngOnDestroy(): void {
        // Stop Meteor sync (autorun + subscription) so no further updates or leaks
        this.stopInstalledAppsSync();

        // Complete the destroy subject to clean up debounced saves
        this.destroy$.next();
        this.destroy$.complete();
        this.saveSubject.complete();

        // Save final session state before destroying
        if (this.currentUserId) {
            this.performSaveToDatabase();
        }

        this.currentRunningAppSubject.next([]);
        this.currentRunningAppSubject.complete();
        this.installedAppSubscription.next([]);
        this.installedAppSubscription.complete();
        this.userProfileSubject.next({} as ProfileUserModel);
        this.userProfileSubject.complete();

        // Clear in-memory cache
        this.appsToRestoreCache = [];
        this.appsRestored = false;
    }
}
